import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ConcretePlan, PlanFactory } from 'src/app/pages/new-billing/models/plan.model';
import { UpgradeDowngradeOnPaidModel } from 'src/app/pages/new-billing/models/upgrade-downgrade-model/upgrade-downgrade-on-paid.model';
import { UpgradeDowngradeOnTrialModel } from 'src/app/pages/new-billing/models/upgrade-downgrade-model/upgrade-downgrade-on-trial.model';
import { LegacyBasePlan } from 'src/app/pages/new-billing/types/plan.types';
import { UpgradeConfirmModalTrackingContext } from 'src/app/pages/new-subscribe-plans/store/model';
import { BillingService } from 'src/app/services/billing/billing.service';
import { PlanStateService } from 'src/app/services/billing/plan-state.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';

/** Target plan for upgrade flows (Standard or Premium). */
export type UpgradePlanType = 'standard' | 'premium';

export interface UpgradeFlowOptions {
  successSnackbarMessage?: string;
  trackingContext?: UpgradeConfirmModalTrackingContext;
}

const UPGRADE_PLAN_TO_BASE_NAME: Record<UpgradePlanType, LegacyBasePlan> = {
  standard: 'standard_plan',
  premium: 'premium_plan',
};

/**
 * Shared service for initiating plan upgrades from upsell flows
 * (e.g. Executive Dashboard upsell, Software Cost Insights upsell, Productivity upsell).
 */
@Injectable({
  providedIn: 'root',
})
export class UpgradeService {
  constructor(
    private readonly billingService: BillingService,
    private readonly planStateService: PlanStateService,
    private readonly growthBook: GrowthBookService,
    private readonly localizedPricingService: LocalizedPricingService,
    private readonly injector: Injector,
  ) {}

  /**
   * Fetches billing data, builds the target plan, and opens the confirm-plan-change
   * dialog ({@link UpgradeBillingDetailsComponent}).
   * Caller should handle loading state and error (e.g. navigate to billing with choosePlan).
   *
   * @param planType - Target plan: 'standard' or 'premium'
   * @param defaultReturnUrl - URL to return to after upgrade (e.g. current page or fallback)
   * @param options - Optional flow overrides (e.g. custom success snackbar message)
   * @returns Observable that completes on success or errors on billing fetch failure
   */
  initiatePlanUpgrade(
    planType: UpgradePlanType,
    defaultReturnUrl: string,
    options?: UpgradeFlowOptions,
  ): Observable<void> {
    const targetBaseName = UPGRADE_PLAN_TO_BASE_NAME[planType];
    return this.billingService.getBillingData('no-cache').pipe(
      switchMap((billingApiData) => {
        const enableDiscountRemoval = this.growthBook.getFeatureValue(
          'billing:discount_removal_companies',
          false,
        );
        const currentPlan = new ConcretePlan(
          billingApiData,
          billingApiData.billingDetails.quantity,
          enableDiscountRemoval,
          this.growthBook,
          this.localizedPricingService,
        );

        const cardPlans = PlanFactory.createCardPlans(currentPlan, this.growthBook);
        const targetCard = cardPlans.find((c) => c.plan.baseName === targetBaseName);
        const targetPlan = targetCard.plan;
        const payRecurrence = currentPlan.paymentModel.paymentRecurrence;
        if (payRecurrence === 'year') {
          targetPlan.switchPlanRecurrence('year');
        }

        const model =
          currentPlan.subscription.status === 'trial'
            ? new UpgradeDowngradeOnTrialModel(this.injector)
            : new UpgradeDowngradeOnPaidModel(this.injector);

        this.planStateService.handlePlanChange(
          { plan: targetPlan, actionType: 'upgrade' },
          model,
          undefined,
          true,
          defaultReturnUrl,
          options?.successSnackbarMessage,
          options?.trackingContext
            ? {
              ...options.trackingContext,
              targetPlan: options.trackingContext.targetPlan ?? planType,
            }
            : undefined,
          currentPlan.billingApiData.billingDetails.currentPlanId,
        );
        return of(undefined);
      }),
    );
  }

  /**
   * Opens the confirm-plan-change dialog immediately using an already-loaded target plan (no billing refetch).
   * Prefer this when the caller already fetched billing (e.g. header upgrade picker).
   */
  confirmPlanUpgrade(
    targetPlan: ConcretePlan,
    currentPlan: ConcretePlan,
    defaultReturnUrl: string,
    options?: UpgradeFlowOptions,
  ): void {
    const plan = targetPlan.clone();
    const payRecurrence = currentPlan.paymentModel.paymentRecurrence;
    if (payRecurrence === 'year') {
      plan.switchPlanRecurrence('year');
    }

    const planType: UpgradePlanType =
      plan.baseName === 'premium_plan' ? 'premium' : 'standard';

    const model =
      currentPlan.subscription.status === 'trial'
        ? new UpgradeDowngradeOnTrialModel(this.injector)
        : new UpgradeDowngradeOnPaidModel(this.injector);

    this.planStateService.handlePlanChange(
      { plan, actionType: 'upgrade' },
      model,
      undefined,
      true,
      defaultReturnUrl,
      options?.successSnackbarMessage,
      options?.trackingContext
        ? {
          ...options.trackingContext,
          targetPlan: options.trackingContext.targetPlan ?? planType,
        }
        : undefined,
      currentPlan.billingApiData.billingDetails.currentPlanId,
    );
  }

  /**
   * Initiates upgrade to the Premium plan.
   * Convenience wrapper around {@link initiatePlanUpgrade} with planType 'premium'.
   *
   * @param defaultReturnUrl - URL to return to after upgrade
   * @returns Observable that completes on success or errors on billing fetch failure
   */
  initiatePremiumUpgrade(defaultReturnUrl: string, options?: UpgradeFlowOptions): Observable<void> {
    return this.initiatePlanUpgrade('premium', defaultReturnUrl, options);
  }

  /**
   * Initiates upgrade to the Standard plan.
   * Convenience wrapper around {@link initiatePlanUpgrade} with planType 'standard'.
   *
   * @param defaultReturnUrl - URL to return to after upgrade
   * @returns Observable that completes on success or errors on billing fetch failure
   */
  initiateStandardUpgrade(defaultReturnUrl: string, options?: UpgradeFlowOptions): Observable<void> {
    return this.initiatePlanUpgrade('standard', defaultReturnUrl, options);
  }
}
